/**
 * Menu JS functionality.
 * Handles the logic needed for the Addsecure megamenu to work.
 *
 * TODO: Implement necessary filters to let child themes modify behaviours etc.
 */

class Menu {
	constructor() {
		this.body = document.querySelector( 'body' );
		this.siteHeader = document.querySelector( '.site-header' );

		// Menu
		this.mainMenuWrapperClass = 'main-menu-wrapper';
		this.mainMenuInnerWrapperClass = 'main-menu-navigation';
		this.megamenuItemClass = 'menu--dropdown';
		this.megamenuItemLinkClass = 'menu-item__link--level-0';
		this.subMenuContainerClass = 'sub-menu-container';
		this.menuItemClass = 'menu-item';
		this.menuItemLinkClass = 'menu-item__link';

		// Sidebar menu
		this.sidebarMenuItemClass = '.menu--with-sidebar .sub-menu-depth-0 > li';
		this.sidebarMenuItemLinkClass = 'menu-item__link--level-1';

		// Mobile menu
		this.mobileToggleClass = 'mobile-nav-toggle';
		this.subMenuToggleClass = 'toggle-sub-menu';

		// Document ready.
		document.defaultView.addEventListener( 'load', this.events );
	}

	/**
	 * Handles registration of events.
	 */
	events = () => {
		// Stamp initial aria-expanded="false" on every submenu toggle button that the PHP did not
		// already annotate. This makes the disclosure-widget role legible to screen readers before
		// any interaction has happened.
		this.body.querySelectorAll( '.main-menu .' + this.subMenuToggleClass ).forEach( button => {
			if ( ! button.hasAttribute( 'aria-expanded' ) ) {
				button.setAttribute( 'aria-expanded', 'false' );
			}
		} );

		// Ensure submenu containers start as inert and aria-hidden so that screen readers cannot
		// navigate into collapsed submenus before any interaction. PHP only does this for
		// megamenu--open-on-tap panels; we cover the remaining containers here.
		//
		// Megamenu (non-sidebar) inner submenus are the exception: on desktop they render as
		// static columns that are visible and interactive whenever the megamenu panel is open.
		// They are not independent disclosure widgets, so they must not carry their own inert
		// state. Instead they inherit it from the megamenu panel via the inert cascade — when the
		// panel is closed its inert disables the whole subtree, and when it opens the subtree
		// becomes interactive again. Stamping inert on them directly would leave them permanently
		// non-interactive even while the panel is open.
		this.body.querySelectorAll( '.main-menu .' + this.subMenuContainerClass ).forEach( submenu => {
			if ( submenu.hasAttribute( 'aria-hidden' ) ) {
				return;
			}

			const isNestedContainer = !! submenu.parentElement?.closest( '.' + this.subMenuContainerClass );
			const inNonSidebarMegamenu = !! submenu.closest( '.' + this.megamenuItemClass ) && ! submenu.closest( '.menu--with-sidebar' );
			if ( isNestedContainer && inNonSidebarMegamenu ) {
				return;
			}

			submenu.setAttribute( 'aria-hidden', 'true' );
			submenu.inert = true;
		} );

		// Handle megamenu toggling
		const megamenuMenuItems = this.body.querySelectorAll( '.' + this.megamenuItemClass );
		megamenuMenuItems?.forEach( item => {
			const megamenuOpenOnTap = item.classList.contains( 'megamenu--open-on-tap' );
			if ( megamenuOpenOnTap ) {
				item.addEventListener( 'click', this.maybeToggleMenu );
			} else {
				[ 'mouseenter', 'focusin' ].forEach( event => {
					item.addEventListener( event, this.closeOtherDropdownMenus );
				} );
			}
			// open on hover is handled via CSS with :hover and :focus-within.
		} );

		// Update ARIA attributes when top-level menus open/close via CSS hover or focus-within
		// on desktop. These items never receive an 'open' class, so handleMenuElementsChange
		// cannot infer the correct state — we must mirror hover/focus events directly into ARIA.
		// Covers both megamenu items (hover variant) and regular dropdown items.
		const hoverOpenMenuItems = this.body.querySelectorAll(
			// Top-level items with submenus, excluding tap-to-open megamenu items.
			'.main-menu > .menu-item-has-children:not(.megamenu--open-on-tap), ' +
			// Nested items within non-megamenu, non-sidebar top-level items
			// (depth ≥ 1 regular dropdown children also open via CSS hover/focus-within).
			'.main-menu > .menu-item:not(.megamenu, .menu--with-sidebar) .menu-item-has-children'
		);
		hoverOpenMenuItems.forEach( item => {
			item.addEventListener( 'mouseenter', () => {
				if ( this.isDesktop() ) {
					this.setHoverMenuAriaState( item, true );
				}
			} );

			// Only collapse ARIA when the mouse leaves AND keyboard focus has also left,
			// so navigating into a submenu with the keyboard after hovering keeps state correct.
			item.addEventListener( 'mouseleave', () => {
				if ( this.isDesktop() && ! item.contains( item.ownerDocument.activeElement ) ) {
					this.setHoverMenuAriaState( item, false );
				}
			} );

			item.addEventListener( 'focusin', () => {
				if ( this.isDesktop() ) {
					this.setHoverMenuAriaState( item, true );
				}
			} );

			// Only collapse ARIA when focus leaves AND the mouse is no longer hovering,
			// so moving from keyboard to mouse navigation keeps state correct.
			item.addEventListener( 'focusout', () => {
				if ( this.isDesktop() ) {
					setTimeout( () => {
						if ( ! item.contains( item.ownerDocument.activeElement ) && ! item.matches( ':hover' ) ) {
							this.setHoverMenuAriaState( item, false );
						}
					}, 50 );
				}
			} );
		} );

		// Handle sidebar menu toggling
		const sidebarMenuItems = this.body.querySelectorAll( this.sidebarMenuItemClass );
		sidebarMenuItems?.forEach( item => {
			item.addEventListener( 'click', this.maybeToggleSidebarMenu );
		} );

		// Handle mobile menu toggle
		const mobileToggles = this.body.querySelectorAll( '.' + this.mobileToggleClass );
		mobileToggles?.forEach( item => {
			item.addEventListener( 'click', this.toggleMobileMenu );
		} );

		// Handle empty menu link toggles.
		const emptyMenuLinks = this.body.querySelectorAll( '.' + this.menuItemLinkClass + '[href=""], .' + this.menuItemLinkClass + '[href="#"]' );
		emptyMenuLinks?.forEach( link => {
			link.addEventListener( 'click', this.HandleEmptyMenuLinkToggle );
		} );

		// Handle submenu toggle
		const submenuToggles = this.body.querySelectorAll( '.' + this.subMenuToggleClass );
		submenuToggles?.forEach( item => {
			item.addEventListener( 'click', this.handleSubMenuToggle );
		} );

		// Handle clicks outside the menu to close it.
		const mainMenuWrapper = document.querySelector( '.' + this.mainMenuInnerWrapperClass );
		mainMenuWrapper?.addEventListener( 'click', this.maybeCloseMenu );
		this.siteHeader?.addEventListener( 'click', this.maybeCloseMenu );

		// Also handle focus out on the main menu wrapper to close it.
		mainMenuWrapper?.addEventListener( 'focusout', this.handleFocusOut );

		// Also handle escape key to close the menu.
		mainMenuWrapper.addEventListener( 'keydown', this.handleEscapeKey );

		// Handle removing inline styles on resizing the sub menu container.
		// This ensures that if the media query changes to be less than 991px, the height is removed.
		const submenuContainers = document.querySelectorAll( '.' + this.subMenuContainerClass );
		submenuContainers?.forEach( container => {
			container.addEventListener( 'resize', this.removeInlineStyleOnResize( container, 'height', '(max-width: 991px)' ) );
		} );

		// Dynamically constrain second-level submenu heights to the viewport.
		this.constrainSubMenuHeights();
	};

	/**
	 * Checks if we are on a desktop size.
	 *
	 * @return {boolean} isDesktop
	 */
	isDesktop = () => {
		return window.matchMedia( '(min-width: 992px)' ).matches;
	};

	/**
	 * Handles clearing and closing an open megamenu open on top link when a user interacts
	 * (hovers or tabs) with another menu--dropdown link.
	 *
	 * @return {void}
	 */
	closeOtherDropdownMenus = () => {
		// there should ony ever be one open, but querySelectorAll just in case.
		const openMenuOnTapLinks = this.body.querySelectorAll( '.megamenu--open-on-tap.open' );

		openMenuOnTapLinks?.forEach( link => {
			// blur both link and child submenutoggle to clear state properly.
			const toggleSubMenuButton = link.querySelector( '.toggle-sub-menu' );

			// for some reason we need to trigger a focus then a blur on both, otherwise it doesn't work.
			toggleSubMenuButton.focus();
			toggleSubMenuButton.blur();
			link.focus();
			link.blur();

			this.toggleMenu( link );
		} );
	};

	/**
	 * Removes an inline style from an element when the screen matches a given media query,
	 * with optimization to prevent performance issues on resize.
	 *
	 * @param {HTMLElement} element    - The element from which the style will be removed.
	 * @param {string}      styleName  - The name of the inline style to remove.
	 * @param {string}      mediaQuery - The media query to check against.
	 */
	removeInlineStyleOnResize = ( element, styleName, mediaQuery ) => {
		const mediaQueryList = window.matchMedia( mediaQuery );

		const handleMediaChange = () => {
			if ( mediaQueryList.matches ) {
				element.style.removeProperty( styleName );
			}
		};

		// Run once on initialization
		handleMediaChange();

		// Listen for media query changes
		mediaQueryList.addEventListener( 'change', handleMediaChange );

		// Cleanup function
		return () => {
			mediaQueryList.removeEventListener( 'change', handleMediaChange );
		};
	};

	/**
	 * Calculates and sets max-height on sub-menu-depth-1 and sub-menu-depth-2 elements
	 * so they are able to be scrolled vertically.
	 *
	 * @return {void}
	 */
	constrainSubMenuHeights = () => {
		const setMaxHeightIfNeeded = subMenu => {
			if ( ! this.isDesktop() ) {
				return;
			}

			const rect = subMenu.getBoundingClientRect();
			const availableHeight = window.innerHeight - rect.top;

			if ( subMenu.scrollHeight > availableHeight ) {
				subMenu.style.maxHeight = availableHeight + 'px';
			} else {
				subMenu.style.removeProperty( 'max-height' );
			}
		};

		const subMenus = this.siteHeader.querySelectorAll(
			'.main-menu > .menu-item:not(.megamenu, .menu--with-sidebar) .sub-menu-depth-1, ' +
			'.main-menu > .menu-item:not(.megamenu, .menu--with-sidebar) .sub-menu-depth-2'
		);

		// set the max-height after opacity transition has finished and the menu is visible.
		subMenus.forEach( subMenu => {
			subMenu.addEventListener( 'transitionend', event => {
				// Only run setMaxHeightIfNeeded when the opacity transition, not for the other 2 transitions. Perf win.
				if ( event.propertyName !== 'opacity' ) {
					return;
				}

				setMaxHeightIfNeeded( subMenu );
			} );
		} );

		// recalculate max-heights on window resize.
		let resizeTimer;
		window.addEventListener( 'resize', () => {
			clearTimeout( resizeTimer );
			resizeTimer = setTimeout( () => {
				if ( ! this.isDesktop() ) {
					subMenus.forEach( subMenu => subMenu.style.removeProperty( 'max-height' ) );
					return;
				}

				subMenus.forEach( subMenu => {
					const computedOpacity = parseFloat( window.getComputedStyle( subMenu ).opacity );
					if ( computedOpacity > 0 ) {
						setMaxHeightIfNeeded( subMenu );
					}
				} );
			}, 250 );
		} );

		// remove max-heights on mobile.
		const mq = window.matchMedia( '(min-width: 992px)' );
		mq.addEventListener( 'change', event => {
			if ( ! event.matches ) {
				subMenus.forEach( subMenu => subMenu.style.removeProperty( 'max-height' ) );
			}
		} );
	};

	/**
	 * Extracts a number from a class in the format 'menu--sidebar-open-{n}'.
	 *
	 * @param {HTMLElement} element - The HTML element to extract the class from.
	 *
	 * @return {number|null} The extracted number, or null if no match is found.
	 */
	extractNumberFromClass = element => {
		if ( ! element || ! element.classList ) {
			return null;
		}

		const regex = /menu--sidebar-open-(\d+)/;

		for ( const className of element.classList ) {
			const match = className.match( regex );
			if ( match ) {
				return parseInt( match[ 1 ], 10 );
			}
		}

		return null;
	};

	/**
	 * Retrieves all sibling elements.
	 * Optionally provide a class name to filter them by further.
	 *
	 * @param {HTMLElement} element   The element to get siblings for.
	 * @param {string}      className The class name to target.
	 */
	getSiblings = ( element, className ) => {
		// Get the parent node of the element
		const parent = element.parentNode;

		// Get all children of the parent node
		const children = parent.children;

		// Convert the HTMLCollection to an array and filter out the element itself
		const siblings = Array.from( children ).filter( child => child !== element );

		// Filter the siblings to get only those with the specified class
		if ( className && typeof className === 'string' ) {
			return siblings.filter( sibling => sibling.classList.contains( className ) );
		}

		return siblings;
	};

	/**
	 * Check if we should close the menu based on whether the click was inside the menu but outside any children.
	 *
	 * @param {Event} event The click event.
	 */
	maybeCloseMenu = event => {
		// If we clicked outside the main menu wrapper and we are on the desktop size, close all open menus.
		if ( this.isDesktop() ) {
			const mainMenuInnerWrapper = document.querySelector( '.' + this.mainMenuInnerWrapperClass );
			if ( mainMenuInnerWrapper && mainMenuInnerWrapper && ! mainMenuInnerWrapper.contains( event.target ) ) {
				this.closeAllMenus();
				return;
			}
		}

		// Only target clicks on the direct main menu wrapper, not any of its children.
		// This should mean that we only close the menu when the user clicks outside of it.
		const mainMenuWrapper = document.querySelector( '.' + this.mainMenuWrapperClass + '.open' );
		if ( ! mainMenuWrapper || ( mainMenuWrapper && mainMenuWrapper !== event.target ) ) {
			return;
		}

		// Toggle 'mobile-menu-open' class on the body element
		this.body.classList.toggle( 'mobile-menu-open' );

		// Set the corresponding class on all possible buttons to reflect the state.
		const mobileToggles = this.body.querySelectorAll( '.' + this.mobileToggleClass );
		mobileToggles?.forEach( item => {
			item?.classList.toggle( 'open' );
		} );

		this.closeAllMenus();
	};

	/**
	 * Close all open menus.
	 *
	 * This effectively resets the whole menu state.
	 */
	closeAllMenus = () => {
		// Get all open submenus.
		const openMenus = document.querySelectorAll( '.' + this.mainMenuWrapperClass + ' .open' );
		openMenus.forEach( menuItem => {
			menuItem.classList.remove( 'open' );
			this.handleMenuElementsChange( menuItem, false );
		} );
	};

	/**
	 * Checks if we should toggle a mega menu!
	 *
	 * @param {Event} event The click event.
	 */
	maybeToggleMenu = event => {
		const target = event.target;

		// The toggle button has its own handler now; don't double-toggle.
		if ( target.closest( '.' + this.subMenuToggleClass ) ) {
			return;
		}

		const parentEl = target.parentElement;

		// Bail if we clicked the submenu container. We don't want to close the parent from the submenu.
		if ( target?.classList.contains( this.subMenuContainerClass ) ) {
			return;
		}

		// If we have a click on the parent LI itself, and its already got the open class, we have likely clicked the psuedo overlay and want to close the menu.
		if ( target.classList.contains( this.megamenuItemClass ) && target.classList.contains( 'open' ) ) {
			event.preventDefault();
			this.toggleMenu( target );
		}

		// Only proceed with logic for opening the megamenu if we are on a desktop sized screen.
		if ( ! this.isDesktop() ) {
			return;
		}

		// Only proceed if the clicked targets direct parent is the link or the li element.
		// this ensures we only action the megamenu from the right elements.
		if (
			parentEl?.classList.contains( this.megamenuItemClass ) ||
			parentEl?.classList.contains( this.megamenuItemLinkClass )
		) {
			event.preventDefault();
			// Make sure we have the parent li element. It is either going to be the parentEl itself, or we get it from the parent tree of the parentEl.
			const parentLi = parentEl?.classList.contains( this.megamenuItemClass ) ? parentEl : parentEl.closest( '.' + this.megamenuItemClass );
			this.toggleMenu( parentLi );
		}
	};

	/**
	 * This ensures that we toggle the megamenu item and also untoggles any previously opened items.
	 *
	 * @param {HTMLElement} menuItem the menu item we want to toggle.
	 */
	toggleMenu = menuItem => {
		// Close any open menu items, both main and submenu
		const openItems = this.body.querySelectorAll( '.' + this.megamenuItemClass + '.open, ' + this.sidebarMenuItemClass + '.open' );

		openItems.forEach( openItem => {
			if ( openItem !== menuItem ) {
				openItem.classList.remove( 'open' );
				this.handleMenuElementsChange( openItem );
			}
		} );

		// Now lets toggle the menu item we interacted with. This could be either opening or closing it.
		menuItem.classList.toggle( 'open' );
		this.handleMenuElementsChange( menuItem );

		// If we open a sidebar menu, we also need to toggle the first submenu item or specifically selected menu.
		// But only on desktop!
		if ( this.isDesktop() ) {
			if ( menuItem.classList.contains( 'open' ) && menuItem.classList.contains( 'menu--with-sidebar' ) ) {
				let submenuLi = menuItem.querySelector( '.sub-menu-depth-0 > li' );

				// Lets see if we actually want to grab a specific child.
				const specificChildIndex = this.extractNumberFromClass( menuItem );
				if ( specificChildIndex !== null ) {
					const alternativeSubmenuLi = menuItem.querySelector( '.sub-menu-depth-0 > li:nth-child(' + ( specificChildIndex ) + ')' );
					if ( alternativeSubmenuLi ) {
						submenuLi = alternativeSubmenuLi;
					}
				}

				if ( submenuLi ) {
					this.toggleSidebarMenu( submenuLi );
				}
			}
		}
	};

	/**
	 * Checks if we should toggle a Sidebar submenu!
	 *
	 * @param {Event} event The click event.
	 */
	maybeToggleSidebarMenu = event => {
		// We only do this on desktop!
		if ( ! this.isDesktop() ) {
			return;
		}

		const target = event.target;
		// ParentEl should be the anchor element, so if we are hitting an anchor we are already where we want to be.
		const parentEl = ( target.tagName === 'A' ) ? target : target.parentElement;
		const isSidebar = parentEl.closest( '.menu--with-sidebar' ) ? true : false;

		// Only proceed if the clicked targets direct parent is the link
		// this ensures we only action the megamenu from the right elements.
		if (
			parentEl?.classList.contains( this.sidebarMenuItemLinkClass ) &&
			isSidebar
		) {
			const parentLi = parentEl.parentElement;

			// Make sure the parent is in fact an LI element.
			if ( parentLi.tagName === 'LI' ) {
				event.preventDefault();
				this.toggleSidebarMenu( parentLi );
			}
		}
	};

	/**
	 * This ensures that we toggle the Sidebar submenu item and also untoggles any previously opened items.
	 *
	 * @param {HTMLElement} menuItem the menu item we want to toggle.
	 */
	toggleSidebarMenu = menuItem => {
		// Close any open menu items
		const openItems = this.body.querySelectorAll( this.sidebarMenuItemClass + '.open' );

		openItems.forEach( openItem => {
			if ( openItem !== menuItem ) {
				if ( ! document.startViewTransition ) {
					openItem.classList.remove( 'open' );
					this.handleMenuElementsChange( openItem, false );
				} else {
					document.startViewTransition( () => {
						openItem.classList.remove( 'open' );
						this.handleMenuElementsChange( openItem, false );
					} );
				}
			} else {
				this.handleMenuElementsChange( openItem );
			}
		} );

		// Now lets toggle open the menu item we interacted with.
		if ( ! document.startViewTransition ) {
			menuItem.classList.add( 'open' );
			this.handleMenuElementsChange( menuItem );
		} else {
			document.startViewTransition( () => {
				menuItem.classList.add( 'open' );
				this.handleMenuElementsChange( menuItem );
			} );
		}

		// Logic for desktop menu only.
		if ( this.isDesktop() ) {
			// We need to dynamically calculate the height of the main sub menu container to match the height of our inner content.
			// Because our inner content is absolutely positioned it does not affect the needed height of its parents.
			const submenu = menuItem.querySelector( ':scope > .' + this.subMenuContainerClass );
			const mainSubmenuContainer = menuItem.closest( '.' + this.megamenuItemClass + ' > .' + this.subMenuContainerClass );
			const mainSubmenuContainerHeight = mainSubmenuContainer?.dataset?.originalHeight ?? mainSubmenuContainer?.offsetHeight;
			const submenuHeight = submenu?.offsetHeight;

			if ( submenuHeight > 0 ) {
				// Store the original height if it does not yet exists.
				// We do this because once we have calculated this thing once, the offsetHeight of the container changes,
				// so our calculations after that wouldn't be correct anymore.
				if ( ! mainSubmenuContainer?.dataset?.originalHeight ) {
					mainSubmenuContainer.dataset.originalHeight = mainSubmenuContainer.offsetHeight;
				}

				// The new height should be the submenus only if its higher than our original height.
				// We also need to add some buffer to it to avoid isues with padding.
				const newHeight = ( submenuHeight > mainSubmenuContainerHeight ) ? submenuHeight : mainSubmenuContainerHeight;
				mainSubmenuContainer.style.height = newHeight + 'px';
			}
		}
	};

	/**
	 * Toggles the menu using the menu toggle button.
	 * This assumes the mobile menu styling applies, otherwise not much will happen.
	 *
	 * @param {Event} event the event toggling the menu.
	 */
	toggleMobileMenu = event => {
		// Generally nothing should happen from our menu toggle anyway, but lets do this to be safe.
		event.preventDefault();

		// Now we simply find our main menu and toggle the open class.
		const mainMenuWrapper = this.body.querySelector( '.' + this.mainMenuWrapperClass );
		mainMenuWrapper?.classList.toggle( 'open' );

		const isOpen = mainMenuWrapper?.classList.contains( 'open' );

		// Toggle 'mobile-menu-open' class on the body element
		this.body.classList.toggle( 'mobile-menu-open', isOpen );

		// Set the corresponding class on all possible buttons to reflect the state.
		const mobileToggles = this.body.querySelectorAll( '.' + this.mobileToggleClass );
		mobileToggles?.forEach( item => {
			item?.classList.toggle( 'open', isOpen );
		} );
	};

	/**
	 * Handles toggling of a sub menu, based on clicking its corresponding menu item link if its href is not set to an URL.
	 *
	 * @param {Event} event The click event.
	 */
	HandleEmptyMenuLinkToggle = event => {
		// We only do this on mobile!
		if ( this.isDesktop() ) {
			return;
		}

		// Find the parent li element which we want to toggle.
		const target = event.target;

		// Verify that the href exists and is not empty or a hash (#).
		const linkHref = target?.getAttribute( 'href' );
		if ( linkHref && linkHref !== '' && linkHref !== '#' ) {
			return;
		}

		event.preventDefault();
		const menuItem = target.closest( '.' + this.menuItemClass );

		// Lets verify that the menu item has a sub menu.
		if ( menuItem?.querySelector( '.' + this.subMenuContainerClass ) ) {
			this.toggleSubMenu( menuItem );
		}
	};

	/**
	 * Handles toggling of a sub menu, based on clicking its corresponding menu toggle.
	 *
	 * @param {Event} event The click event.
	 */
	handleSubMenuToggle = event => {
		// Let's preventDefault just to be safe.
		event.preventDefault();

		// Find the parent li element which we want to toggle.
		const target = event.target;
		const menuItem = target.closest( '.' + this.menuItemClass );

		if ( menuItem ) {
			this.toggleSubMenu( menuItem );
		}
	};

	/**
	 * Handles toggling of a sub menu.
	 *
	 * @param {HTMLElement} menuItem The clicked menu item.
	 */
	toggleSubMenu = menuItem => {
		// Find any sibling menu items with the open class and reset them.
		const siblingsToClose = this.getSiblings( menuItem, 'open' );
		siblingsToClose?.forEach( sibling => {
			if ( ! document.startViewTransition ) {
				sibling.classList.remove( 'open' );
				this.handleMenuElementsChange( sibling );
			} else {
				document.startViewTransition( () => {
					sibling.classList.remove( 'open' );
					this.handleMenuElementsChange( sibling );
				} );
			}
		} );

		// Open the menu item.
		if ( ! document.startViewTransition ) {
			menuItem?.classList.toggle( 'open' );
			this.handleMenuElementsChange( menuItem );
		} else {
			document.startViewTransition( () => {
				menuItem?.classList.toggle( 'open' );
				this.handleMenuElementsChange( menuItem );
			} );
		}
	};

	/**
	 * Handles inner elements of a menu item element.
	 *
	 * @param {HTMLElement} menuItem The element which we want to manipulate.
	 * @param {boolean}     toggle   If we want to set closed regardless we set this to false.
	 */
	handleMenuElementsChange = ( menuItem, toggle = true ) => {
		const button = menuItem.querySelector( '.' + this.subMenuToggleClass );
		const submenu = menuItem.querySelector( '.' + this.subMenuContainerClass );
		let toggleValue = menuItem?.classList.contains( 'open' );
		if ( toggle === false ) {
			toggleValue = false;
		}

		button?.setAttribute( 'aria-expanded', toggleValue );
		if ( submenu ) {
			submenu?.setAttribute( 'aria-hidden', ! toggleValue );
			submenu.inert = ! toggleValue;
		}
	};

	/**
	 * Directly sets ARIA attributes for a menu item that is opened/closed by CSS hover or
	 * focus-within rather than by a JS click.
	 *
	 * These items never receive an 'open' class, so handleMenuElementsChange cannot derive the
	 * correct state from the DOM. This method accepts the intended state as an explicit argument.
	 *
	 * The inert property is mirrored in both directions: removed on open so keyboard and screen
	 * reader users can navigate into the submenu, and re-applied on close so that screen readers
	 * in browse/virtual mode cannot navigate into visually-hidden collapsed submenus.
	 *
	 * @param {HTMLElement} menuItem The menu-item element (any depth).
	 * @param {boolean}     isOpen   True when the submenu is becoming visible, false when hidden.
	 */
	setHoverMenuAriaState = ( menuItem, isOpen ) => {
		const button = menuItem.querySelector( ':scope > .' + this.subMenuToggleClass );
		const submenu = menuItem.querySelector( ':scope > .' + this.subMenuContainerClass );

		button?.setAttribute( 'aria-expanded', isOpen );

		if ( submenu ) {
			submenu.setAttribute( 'aria-hidden', ! isOpen );
			submenu.inert = ! isOpen;
		}
	};

	/**
	 * Handles focus out events for the main menu.
	 * If we focus outside the menu, we need to close it.
	 *
	 * @param {Event} event The focus out event.
	 */
	handleFocusOut = event => {
		const mainMenuWrapper = document.querySelector( '.' + this.mainMenuWrapperClass );

		setTimeout( () => {
			const activeElement = event.target?.ownerDocument?.activeElement ?? null;

			if ( this.isDesktop() && ! mainMenuWrapper.contains( activeElement ) ) {
				this.closeAllMenus();
			}
		}, 50 );
	};

	/**
	 * Handles Escape key to close open menus and return focus to toggle.
	 *
	 * @param {KeyboardEvent} event
	 */
	handleEscapeKey = event => {
		if ( event.key !== 'Escape' || ! this.isDesktop() ) {
			return;
		}

		const openMenuItem = this.body.querySelector( '.' + this.megamenuItemClass + '.open' );
		if ( openMenuItem ) {
			const toggleButton = openMenuItem.querySelector( '.' + this.subMenuToggleClass );
			this.closeAllMenus();
			toggleButton?.focus();
		}
	};
}

export default Menu;
